import { injectIntl, IntlShape } from 'react-intl';
import { translate } from 'containers/services/translate';
import messages from 'containers/CourtManagerLegalProceedings/Provision/Regulation/messages';

import { Listings, SagisConfig } from 'containers/AppContainer/typings';
import {
  Title,
  Field,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/styled-components';
import {
  StyledContent,
  StyledData,
  StyledRecapData,
  StyledInfoContent,
} from 'containers/CourtManagerLegalProceedings/Provision/styled-components';

import { get, isEmpty, isNull, uniqueId } from 'lodash';
import { resolveLabel } from 'containers/CourtManagerLegalProceedings/Provision/utils';
import { FormControl, Grid, TextField } from '@material-ui/core';
import {
  getBeneficiaryRecipient,
  getRealBeneficiary,
  getPaymentChannel,
  numberWithCommas,
  getVictims,
  isMinor,
  getRegulationRecapInfo,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/utils';
import {
  REGULATIONS,
  REGULATION_AMOUNTS,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/data-structure';
import { RcCorporeal } from 'containers/CourtManagerLegalProceedings/Provision/Regulation/typings';
import { memo } from 'react';
import { EXPAND_RISK } from 'containers/CourtManagerLegalProceedings/Provision/Regulation/Recap/constants';
import SahamAccordion from 'components/SahamAccordion';
import {
  enableRemainingAmountField,
  getProvisionExecutionValue,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/Recap/utils';
import { INJURY_POST_DECEASED } from 'containers/PreOpeningPage/constants';
import {
  FEES,
  RECIPIENT,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/constants';
import DataRecap from 'containers/CourtManagerLegalProceedings/Provision/Regulation/Recap/DataRcap';
import { VictimType } from 'typings/globalTypes';

export interface AdultRegulationRecapProps {
  intl: IntlShape;
  listings: Listings;
  rcCorporeal: RcCorporeal;
  encryptedDocumentsOfAdults: any;
  corpoEncryptedDocuments: any;
  substituteDocumentNature: SagisConfig[];
  corpoEncryptedDocumentsResult: any;
  handleChange: Function;
  expanded: any;
  handleChangeExpanded: Function;
  lossDate: any;
  setEncryptedDocumentsOfAdults: any;
  regulations: any;
  corpoRegulations: any;
  isRecap?: boolean;
}

export const AdultRegulationRecap: React.FC<AdultRegulationRecapProps> = memo(
  ({
    rcCorporeal,
    listings,
    corpoEncryptedDocuments,
    substituteDocumentNature,
    corpoEncryptedDocumentsResult,
    handleChangeExpanded,
    expanded,
    lossDate,
    regulations = [],
    isRecap = false,

    ...props
  }): JSX.Element => {
    const showRemainingAmountField = enableRemainingAmountField(
      get(rcCorporeal, 'regulationInfo.natureCode'),
    );
    const {
      encryptedDocumentsOfAdults,
      totalAmount,
      newAmount,
    } = props.encryptedDocumentsOfAdults;

    return (
      <SahamAccordion
        key={rcCorporeal.regulationId}
        title={translate(messages.adultVictims, props)}
        expanded={get(
          expanded[rcCorporeal.regulationId],
          [EXPAND_RISK.ADULT],
          false,
        )}
        onChange={handleChangeExpanded(rcCorporeal, [EXPAND_RISK.ADULT])}
      >
        <div>
          <StyledInfoContent>
            <Title className="recap-title">
              {translate(messages.judgementInformation, props)}
            </Title>
            <Grid container spacing={1} direction="row">
              <Grid item xs={3}>
                <Title>{translate(messages.recipient, props)}</Title>
                <Field>
                  {getBeneficiaryRecipient(
                    rcCorporeal,
                    get(listings, 'EncryptDocumentCity', []),
                    get(rcCorporeal, 'regulationInfo.isRecap', false),
                  )}
                </Field>
              </Grid>
              <Grid item xs={3}>
                <Title>
                  {get(
                    rcCorporeal,
                    'regulationInfo.beneficiaryRecipient',
                    '',
                  ) === RECIPIENT.CDRA
                    ? translate(messages.cdraRealBeneficiary, props)
                    : translate(messages.realBeneficiary, props)}
                </Title>
                <Field>
                  {getRealBeneficiary(
                    rcCorporeal,
                    get(listings, 'EncryptDocumentCity', []),
                    get(rcCorporeal, 'regulationInfo.isRecap', false),
                  )}
                </Field>
              </Grid>
              <Grid item xs={3}>
                <Title>{translate(messages.paymentChannel, props)}</Title>
                <Field>{getPaymentChannel(listings, rcCorporeal)}</Field>
              </Grid>
              <Grid item xs={3}>
                <Title>{translate(messages.additionalAddress, props)}</Title>
                <Field>
                  {get(
                    rcCorporeal.regulationInfo,
                    [REGULATIONS.ADDITIONAL_ADDRESS],
                    '',
                  )}
                </Field>
              </Grid>
              <Grid item xs={2}>
                <Title>{translate(messages.totalAmount, props)}</Title>
                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    onChange={props.handleChange(
                      props.encryptedDocumentsOfAdults,
                      props.setEncryptedDocumentsOfAdults,
                    )}
                    data-testid={REGULATION_AMOUNTS.AMOUNT}
                    value={
                      newAmount === 0 || isEmpty(newAmount)
                        ? numberWithCommas(String(totalAmount.toFixed(3)))
                        : numberWithCommas(String(newAmount))
                    }
                    disabled
                  />
                </FormControl>
              </Grid>
            </Grid>
          </StyledInfoContent>

          {isRecap ? (
            <DataRecap {...props} rcCorpo={rcCorporeal} />
          ) : (
            getVictims(encryptedDocumentsOfAdults, rcCorporeal).map(
              (victim: VictimType) => {
                return (
                  <StyledContent>
                    <div>
                      <StyledData key={get(victim, 'id', uniqueId())}>
                        {`${resolveLabel(victim)} ${
                          isEmpty(get(victim, 'beneficiaries', [])) &&
                          isMinor(
                            victim,
                            { victim },
                            rcCorporeal,
                            get(
                              rcCorporeal.regulationInfo,
                              REGULATIONS.REGULATION_DATE,
                              [],
                            ),
                          )
                            ? `(${translate(messages.minor, props)})`
                            : ''
                        }`}
                      </StyledData>
                      <Grid container spacing={1} direction="row">
                        {get(
                          rcCorporeal.regulationInfo,
                          [REGULATIONS.IS_APPEAL],
                          '',
                        ) &&
                          get(victim, 'injuryPostKey') !==
                            INJURY_POST_DECEASED && (
                            <>
                              <Grid item xs={3}>
                                <Title>
                                  {translate(
                                    messages.appealCompensationAmount,
                                    props,
                                  )}
                                </Title>
                                <Field>
                                  {numberWithCommas(
                                    String(
                                      get(
                                        victim.regulationAmount,
                                        [REGULATION_AMOUNTS.APPEAL_AMOUNT],
                                        '',
                                      ),
                                    ),
                                  )}
                                </Field>
                              </Grid>
                              {!isEmpty(
                                get(
                                  victim.regulationAmount,
                                  [REGULATION_AMOUNTS.APPEAL_REASON],
                                  '',
                                ),
                              ) && (
                                <Grid item xs={3}>
                                  <Title>
                                    {translate(messages.appealReason, props)}
                                  </Title>
                                  <Field>
                                    {get(
                                      victim.regulationAmount,
                                      [REGULATION_AMOUNTS.APPEAL_REASON],
                                      '',
                                    )}
                                  </Field>
                                </Grid>
                              )}
                            </>
                          )}
                        {!isNull(
                          get(
                            victim.regulationAmount,
                            [REGULATION_AMOUNTS.COMPENSATION_AMOUNT],
                            null,
                          ),
                        ) &&
                          get(victim, 'injuryPostKey') !==
                            INJURY_POST_DECEASED && (
                            <>
                              <Grid item xs={3}>
                                <Title>
                                  {translate(
                                    messages.judgementCompensationAmount,
                                    props,
                                  )}
                                </Title>
                                <Field>
                                  {numberWithCommas(
                                    String(
                                      get(
                                        victim.regulationAmount,
                                        [
                                          REGULATION_AMOUNTS.COMPENSATION_AMOUNT,
                                        ],
                                        '',
                                      ),
                                    ),
                                  )}
                                </Field>
                              </Grid>
                              {!isEmpty(
                                get(
                                  victim.regulationAmount,
                                  [REGULATION_AMOUNTS.JUDGEMENT_REASON],
                                  '',
                                ),
                              ) && (
                                <Grid item xs={3}>
                                  <Title>
                                    {translate(messages.judgementReason, props)}
                                  </Title>
                                  <Field>
                                    {get(
                                      victim.regulationAmount,
                                      [REGULATION_AMOUNTS.JUDGEMENT_REASON],
                                      '',
                                    )}
                                  </Field>
                                </Grid>
                              )}
                            </>
                          )}
                        {!isEmpty(
                          get(
                            victim.regulationAmount,
                            [REGULATION_AMOUNTS.COMPANY_AMOUNT],
                            '',
                          ),
                        ) && (
                          <Grid item xs={3}>
                            <Title>
                              {translate(
                                messages.judgementCompanyAmount,
                                props,
                              )}
                            </Title>
                            <Field>
                              {numberWithCommas(
                                get(
                                  victim.regulationAmount,
                                  [REGULATION_AMOUNTS.COMPANY_AMOUNT],
                                  '',
                                ),
                              )}
                            </Field>
                          </Grid>
                        )}
                        {!showRemainingAmountField && (
                          <Grid item xs={2}>
                            <Title>{translate(messages.rate, props)}</Title>
                            <Field>
                              {`${get(
                                victim.regulationAmount,
                                [REGULATION_AMOUNTS.RATE],
                                '',
                              )}%`}
                            </Field>
                          </Grid>
                        )}
                        {get(victim, 'injuryPostKey') ===
                          INJURY_POST_DECEASED &&
                          !!get(
                            victim.regulationAmount,
                            [REGULATION_AMOUNTS.MEDICAL_FEES],
                            '',
                          ) && (
                            <Grid item xs={2}>
                              <Title>
                                {translate(messages.medicalFees, props)}
                              </Title>
                              <Field>
                                {getProvisionExecutionValue(
                                  FEES.MEDICAL_FEES,
                                  victim,
                                  rcCorporeal,
                                  corpoEncryptedDocumentsResult,
                                )}
                              </Field>
                            </Grid>
                          )}
                        {get(victim, 'injuryPostKey') ===
                          INJURY_POST_DECEASED &&
                          !!get(
                            victim.regulationAmount,
                            [REGULATION_AMOUNTS.FUNERAL_FEES],
                            '',
                          ) && (
                            <Grid item xs={2}>
                              <Title>
                                {translate(messages.funeralFees, props)}
                              </Title>
                              <Field>
                                {getProvisionExecutionValue(
                                  FEES.FUNERAL_FEES,
                                  victim,
                                  rcCorporeal,
                                  corpoEncryptedDocumentsResult,
                                )}
                              </Field>
                            </Grid>
                          )}
                        {!isEmpty(get(victim, 'beneficiaries', [])) && (
                          <Grid item xs={9}>
                            <Title>
                              {translate(messages.beneficiariesTitle, props)}
                            </Title>
                            {get(victim, 'beneficiaries', []).map(
                              (nextOfKin: any) => (
                                <div className="beneficiary-item">
                                  <StyledRecapData
                                    isColored={false}
                                    className="item"
                                    key={get(nextOfKin, 'id', uniqueId())}
                                  >
                                    {`${get(nextOfKin, 'lastName', '')} ${get(
                                      nextOfKin,
                                      'firstName',
                                      '',
                                    )} ${
                                      isMinor(
                                        victim,
                                        { nextOfKin },
                                        rcCorporeal,
                                        get(
                                          rcCorporeal.regulationInfo,
                                          REGULATIONS.REGULATION_DATE,
                                          [],
                                        ),
                                      )
                                        ? `(${translate(
                                            messages.minor,
                                            props,
                                          )})`
                                        : ''
                                    }`}
                                  </StyledRecapData>
                                  {get(
                                    rcCorporeal.regulationInfo,
                                    [REGULATIONS.IS_APPEAL],
                                    '',
                                  ) && (
                                    <div style={{ width: '100%' }}>
                                      <Title>
                                        {translate(
                                          messages.beneficiaryAppealAmount,
                                          props,
                                        )}
                                      </Title>
                                      <Field>
                                        {get(
                                          getRegulationRecapInfo(
                                            get(victim, 'victimKey', ''),
                                            get(nextOfKin, 'nextOfKinKey', ''),
                                            corpoEncryptedDocumentsResult,
                                          ),
                                          'arrestAmount',
                                          '',
                                        )}
                                      </Field>
                                    </div>
                                  )}
                                  <div style={{ width: '100%' }}>
                                    <Title>
                                      {translate(
                                        messages.beneficiaryJudgementAmount,
                                        props,
                                      )}
                                    </Title>
                                    <Field>
                                      {get(
                                        getRegulationRecapInfo(
                                          get(victim, 'victimKey', ''),
                                          get(nextOfKin, 'nextOfKinKey', ''),
                                          corpoEncryptedDocumentsResult,
                                        ),
                                        'judgementAmount',
                                        '',
                                      )}
                                    </Field>
                                  </div>
                                  <div style={{ width: '100%' }}>
                                    <Title>
                                      {translate(
                                        messages.provisionalExecutionAmount,
                                        props,
                                      )}
                                    </Title>
                                    <Field>
                                      {get(
                                        getRegulationRecapInfo(
                                          get(victim, 'victimKey', ''),
                                          get(nextOfKin, 'nextOfKinKey', ''),
                                          corpoEncryptedDocumentsResult,
                                        ),
                                        'provisionalExecutionAmount',
                                        '',
                                      )}
                                    </Field>
                                  </div>

                                  {showRemainingAmountField && (
                                    <div style={{ width: '100%' }}>
                                      <Title>
                                        {translate(
                                          messages.remainderAmount,
                                          props,
                                        )}
                                      </Title>
                                      <Field>
                                        {get(
                                          getRegulationRecapInfo(
                                            get(victim, 'victimKey', ''),
                                            get(nextOfKin, 'nextOfKinKey', ''),
                                            corpoEncryptedDocumentsResult,
                                          ),
                                          'remainderAmount',
                                          '',
                                        )}
                                      </Field>
                                    </div>
                                  )}
                                </div>
                              ),
                            )}
                          </Grid>
                        )}

                        {isEmpty(get(victim, 'beneficiaries', [])) && (
                          <>
                            <Grid item xs={4}>
                              <Title>
                                {translate(
                                  messages.provisionalExecutionAmount,
                                  props,
                                )}
                              </Title>
                              <Field>
                                {get(
                                  getRegulationRecapInfo(
                                    victim.victimKey,
                                    -1,
                                    corpoEncryptedDocumentsResult,
                                  ),
                                  'provisionalExecutionAmount',
                                  '',
                                )}
                              </Field>
                            </Grid>
                            {showRemainingAmountField && (
                              <Grid item xs={3}>
                                <Title>
                                  {translate(messages.remainder, props)}
                                </Title>
                                <Field>
                                  {get(
                                    getRegulationRecapInfo(
                                      victim.victimKey,
                                      -1,
                                      corpoEncryptedDocumentsResult,
                                    ),
                                    'remainderAmount',
                                    '',
                                  )}
                                </Field>
                              </Grid>
                            )}
                          </>
                        )}
                      </Grid>
                    </div>
                  </StyledContent>
                );
              },
            )
          )}
        </div>
      </SahamAccordion>
    );
  },
);
export default injectIntl(AdultRegulationRecap);
